import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import Input from '@material-ui/core/Input';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import api from '~/services';
import log from '~/helpers/log';
import * as lib from '~/dependencies';
import { ExtensionInfo } from '~/interfaces';
import * as actionTypes from '~/store/actions/actionTypes';
import { filterOptions } from '~/store/reducers/historyFilterOptions';

import {
  FormControl,
  MenuProps,
  MenuItem,
  ListItemText,
} from '../sharedStyles';

export default function ExtensionSelector() {
  const selectorName = 'Extensions';

  const dispatch = useDispatch();

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [extensions, setExtensions] = useState<ExtensionInfo[]>([]);

  const selectedExtensions = useSelector(
    (state: RootStateOrAny) => state.historyFilterOptions.extensions
  );

  const getAllowedExtensions = useCallback(async () => {
    try {
      const data: ExtensionInfo[] = (await api.accounts.get.allowedExtensions())
        ?.data;
      setExtensions(lib._.sortBy(data, 'number'));
    } catch (err) {
      log.error('Failed to fetch allowed extensions.', err);
    }
    return [];
  }, []);

  useEffect(() => {
    if (!isFetching) {
      setIsFetching(true);
      getAllowedExtensions();
    }
  }, [isFetching, getAllowedExtensions]);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newSelectedExtensions = event.target.value as string[];

    dispatch({
      type: actionTypes.SET_HISTORY_FILTER_OPTIONS,
      option: filterOptions.extensions,
      payload: newSelectedExtensions,
      extra: { resetCallItems: true },
    });
  };

  const handleToggle = (state: boolean) => {
    setOpen(state);
  };

  const handlePlaceholder = () => {
    const hasSelectedExt = selectedExtensions.length > 0;
    if (open || (hasSelectedExt && !open)) {
      return selectedExtensions;
    }
    return [selectorName];
  };

  const renderValue = (values: string | string[]) => {
    if (typeof values === 'object' && values[0] !== selectorName) {
      return `Ext: ${(values as string[]).join(', ')}`;
    }
    return values;
  };

  return (
    <div>
      <FormControl
        variant="outlined"
        hasselecteditems={selectedExtensions?.length}>
        <Select
          id="select-extensions"
          labelId="select-extensions"
          multiple
          disableUnderline
          input={<Input />}
          onOpen={() => handleToggle(true)}
          onClose={() => handleToggle(false)}
          onChange={handleChange}
          value={handlePlaceholder()}
          // @ts-ignore
          renderValue={renderValue}
          // @ts-ignore
          MenuProps={MenuProps}>
          <MenuItem firstItem value={selectorName} disabled>
            <ListItemText firstItem primary={selectorName} />
          </MenuItem>
          {extensions.map(ext => (
            <MenuItem key={ext.number} value={ext.number}>
              <Checkbox checked={selectedExtensions.indexOf(ext.number) > -1} />
              <ListItemText>
                Ext: <b>{ext.number}</b> {ext.name}
              </ListItemText>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}
